import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as templatePageCategoryActions from '@redux/modules/templatePageCategory'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/templatePageCategory'
import type { TemplatePageCategoryModel, TemplatePageCategoryRequestOptions } from '@models/types'

export const generateUrls = () => ({
  hiddenTemplatePageCategoriesIndexUrl: '#/systemManager/templateCategories?hidden=true',
  templatePageCategoriesIndexUrl: '#/systemManager/templateCategories?hidden=false',
})

type CreateTemplatePageCategoryParams = {
  dispatch: AppDispatch,
  templatePageCategoryParams: Partial<TemplatePageCategoryModel>,
  requestOptions?: TemplatePageCategoryRequestOptions,
}

const createTemplatePageCategory = (params: CreateTemplatePageCategoryParams) => {
  const { templatePageCategoryParams, dispatch, requestOptions } = params
  const { createTemplatePageCategory: createFn } = templatePageCategoryActions

  return dispatch(createFn(templatePageCategoryParams, requestOptions))
}

type UpdateTemplatePageCategoryParams = {
  dispatch: AppDispatch,
  templatePageCategory: TemplatePageCategoryModel,
  templatePageCategoryParams: Partial<TemplatePageCategoryModel>,
  requestOptions?: TemplatePageCategoryRequestOptions,
}

const updateTemplatePageCategory = (params: UpdateTemplatePageCategoryParams) => {
  const {
    dispatch, templatePageCategory, templatePageCategoryParams, requestOptions,
  } = params
  const { updateTemplatePageCategory: updateFn } = templatePageCategoryActions

  const updatedParams = mergeAndStringifyEntityObjects(templatePageCategory, templatePageCategoryParams, [])

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTemplatePageCategoryParams = {
  templatePageCategory: DeleteParams<TemplatePageCategoryModel>,
  dispatch: AppDispatch,
}

const deleteTemplatePageCategory = (params: DeleteTemplatePageCategoryParams) => {
  const { templatePageCategory, dispatch } = params
  const { deleteTemplatePageCategory: deleteFn } = templatePageCategoryActions

  return dispatch(deleteFn(templatePageCategory))
}

type CreateOrEditTemplatePageCategoryPayload = {
  templatePageCategory: TemplatePageCategoryModel,
  showCreateOrEditTemplatePageCategoryModal: Function,
}

const createOrEditTemplatePageCategory = (params: CreateOrEditTemplatePageCategoryPayload) => new Promise((resolve, reject) => {
  const { templatePageCategory, showCreateOrEditTemplatePageCategoryModal } = params

  if (showCreateOrEditTemplatePageCategoryModal){
    const payload = {
      templatePageCategory,
    }

    showCreateOrEditTemplatePageCategoryModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditTemplatePageCategoryModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTemplatePageCategoryForm(
  templatePageCategory: Partial<TemplatePageCategoryModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const templatePageCategoryForm = useForm(
    defaultFormState,
    { entity: templatePageCategory, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [templatePageCategory.id, templatePageCategory.cache_key],
  )

  return {
    ...templatePageCategoryForm,
  }
}

type LatestEntity = { entity: TemplatePageCategoryModel }

function useTemplatePageCategory(initEntity: Partial<TemplatePageCategoryModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: templatePageCategory }: LatestEntity = useLatestEntity(initEntity, 'templatePageCategories')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditTemplatePageCategoryModal } = callbacks || {}

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.templatePageCategories)

  return {
    callbacks: {
      createTemplatePageCategory: (
        templatePageCategoryParams: Partial<TemplatePageCategoryModel>,
        requestOptions?: TemplatePageCategoryRequestOptions,
      ) => (
        createTemplatePageCategory({ templatePageCategoryParams, dispatch, requestOptions })
      ),
      createOrEditTemplatePageCategory: () => (
        createOrEditTemplatePageCategory({ templatePageCategory, showCreateOrEditTemplatePageCategoryModal })
      ),
      deleteTemplatePageCategory: () => deleteTemplatePageCategory({ templatePageCategory, dispatch }),
      updateTemplatePageCategory: (
        templatePageCategoryParams: Partial<TemplatePageCategoryModel>,
        requestOptions?: TemplatePageCategoryRequestOptions,
      ) => (
        updateTemplatePageCategory({
          templatePageCategory, templatePageCategoryParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    deleting,
    templatePageCategory,
    loading,
    updating,
  }
}

export default useTemplatePageCategory
